import React, { PropsWithChildren, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { makeStyles } from '@material-ui/core'

type PlacementType = 'top-start' | 'top-center' | 'bottom-start' | 'bottom-center' | 'bottom-left' | 'bottom-right'

interface PositionInterface {
    top: number
    left: number
    transform: string
}

interface PropsInterface {
    open: boolean
    anchorEl: HTMLElement | null
    placement?: PlacementType
    offset?: number
    className?: string
    childrenClassName?: string
    onClose?(): void
    'data-testid'?: string
}

const getPosition = (
    anchorEl: HTMLElement,
    placement: PlacementType,
    offset: number
): PositionInterface => {
    const rect = anchorEl.getBoundingClientRect()

    switch (placement) {
            case 'top-center':
                return {
                    top: rect.top - offset,
                    left: rect.left + (rect.width / 2),
                    transform: 'translate(-50%, -100%)'
                }
            case 'bottom-start':
                return {
                    top: rect.bottom + offset,
                    left: rect.left,
                    transform: 'translate(0, 0)'
                }
            case 'bottom-center':
                return {
                    top: rect.bottom + offset,
                    left: rect.left + (rect.width / 2),
                    transform: 'translate(-50%, 0)'
                }
            case 'bottom-left':
                return {
                    top: rect.bottom + offset,
                    left: rect.left - (rect.width / 2),
                    transform: 'translate(-50%, 0)'
                }
            case 'bottom-right':
                return {
                    top: rect.bottom + offset,
                    left: rect.left + (rect.width / 2),
                    transform: 'translate(0, 0)'
                }
            case 'top-start':
            default:
                return {
                    top: rect.top - offset,
                    left: rect.left,
                    transform: 'translate(0, -100%)'
                }
    }
}

const jss = () => {
    return {
        popover: {
            position: 'fixed',
            zIndex: 1300,
            pointerEvents: 'none'
        },
        content: {
            width: 'max-content',
            maxWidth: 'inherit'
        }
    }
}

const useStyles = makeStyles(jss)

/**
 *
 */
const Popover = ({
    open,
    anchorEl,
    placement = 'top-start',
    offset = 8,
    className,
    childrenClassName,
    children,
    onClose,
    'data-testid': dataTestId = 'popover'
}: PropsWithChildren<PropsInterface>): JSX.Element => {
    const styles = useStyles()
    const [position, setPosition] = useState<PositionInterface | null>(null)

    const updatePosition = useMemo(() => {
        return () => {
            if (!anchorEl) {
                setPosition(null)
                return
            }

            setPosition(getPosition(anchorEl, placement, offset))
        }
    }, [anchorEl, placement, offset])

    useLayoutEffect(() => {
        if (!open || !anchorEl) {
            setPosition(null)
            return
        }

        updatePosition()
    }, [open, anchorEl, updatePosition])

    useEffect(() => {
        if (!open || !anchorEl) return

        const handleScroll = () => updatePosition()
        const handleResize = () => updatePosition()

        window.addEventListener('scroll', handleScroll, true)
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('scroll', handleScroll, true)
            window.removeEventListener('resize', handleResize)
        }
    }, [open, anchorEl, updatePosition])

    useEffect(() => {
        if (!open || !onClose) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [open, onClose])

    if (!open || !anchorEl || !position || typeof document === 'undefined') {
        return <></>
    }

    return ReactDOM.createPortal(
        <div
            className={`${styles.popover} ${className || ''}`}
            style={{
                top: position.top,
                left: position.left,
                transform: position.transform
            }}
            data-testid={dataTestId}
        >
            <div className={`${styles.content} ${childrenClassName || ''}`} data-testid={`${dataTestId}-content`}>
                {children}
            </div>
        </div>,
        document.body
    )
}

export default Popover
